import type { Messages } from "../types";

export const messages: Messages = {
  shared: {
    title: "Compte client Fielmann",
    tabs: {
      login: "Se connecter",
      signup: "Créer un compte",
      ariaLabel: "Authentification",
    },
    languageSwitcher: {
      label: "Choisir la langue",
    },
    password: {
      show: "Afficher le mot de passe",
      hide: "Masquer le mot de passe",
    },
    header: {
      logoAriaLabel: "Fielmann",
    },
    footer: {
      ariaLabel: "Pied de page",
      customerService: "Service client",
      legalNotice: "Mentions légales",
      dataProtection: "Protection des données",
    },
    loading: {
      ariaLabel: "Un instant…",
      content: "Chargement en cours...",
    },
    genericError: "Une erreur est survenue. Réessaie plus tard.",
  },
  login: {
    subtitle: "Connecte-toi avec ton adresse e-mail et ton mot de passe.",
    form: {
      emailLabel: "E-mail *",
      passwordLabel: "Mot de passe *",
      forgotPassword: "Mot de passe oublié ?",
      submitButton: "Se connecter",
      credentialError:
        "L’adresse e-mail et le mot de passe ne correspondent pas.",
    },
  },
  signup: {
    subtitle: {
      before: "Profite des ",
      highlight: "avantages",
      after: " et crée ton compte client Fielmann.",
    },
    benefits: {
      popoverHeading: "Tes avantages :",
      closeLabel: "Fermer",
      items: {
        orders:
          "Toutes tes commandes Fielmann et leur statut en un coup d’œil.",
        visionValues:
          "Reprise des valeurs visuelles pour la prochaine commande.",
        appointments: "Prendre et gérer les rendez-vous.",
        reorders: "Effectuer de nouvelles commandes en toute simplicité.",
      },
    },
    form: {
      firstNameLabel: "Prénom *",
      lastNameLabel: "Nom *",
      emailLabel: "E-mail *",
      passwordLabel: "Mot de passe *",
      passwordRepeatLabel: "Confirmer le mot de passe *",
      submitButton: "Créer un compte",
      errorTitle: "Le compte n’a pas pu être créé.",
      errorMessage: "Vérifie tes informations ou réessaie plus tard.",
    },
    consents: {
      requiredError: "Donne ton consentement pour continuer.",
      unavailableError: "Une erreur est survenue. Réessaie plus tard.",
      required: {
        headline: "Consentement relatif au compte client",
        subline:
          "Pour que nous puissions afficher toutes les informations dans ton compte client, comme tes achats ou tes rendez-vous, nous avons besoin de ton consentement.",
      },
      optional: {
        headline: "Souhaites-tu recevoir des publicités personnalisées ?",
        subline:
          "À toi de choisir : si tu donnes ton consentement, tu recevras des offres et des informations personnalisées. Sans ton consentement, tu peux utiliser ton compte client sans recevoir de publicité personnalisée.",
      },
    },
  },
  emailVerification: {
    subtitle: "Tu n’arrives pas à te connecter ?",
    alertTitle: "Confirme d’abord ton adresse e-mail.",
    alertBody:
      "Confirme d’abord ton adresse e-mail pour activer ton compte Fielmann.",
    alertSpamHint: "Tu n’as pas reçu d’e-mail ? Vérifie aussi dans tes spams.",
    resendButton: "Recevoir un nouvel e-mail",
    backToLogin: "Revenir à la page de connexion",
  },
  resetPasswordRequest: {
    subtitle: "Mot de passe oublié ?",
    body: "Saisis l’adresse e-mail utilisée pour créer ton compte. Nous t’enverrons un lien qui te permettra de réinitialiser ton mot de passe en toute simplicité.",
    emailLabel: "E-mail *",
    submitButton: "Recevoir le lien",
    backToLogin: "Revenir à la page de connexion",
    banner: {
      closeAria: "Fermer",
      rateLimit: {
        title: "Trop de demandes de réinitialisation",
        body: "Vérifie ta boîte de réception et tes spams pour retrouver l’e-mail de réinitialisation de ton mot de passe.",
        waitHint:
          "Tu ne l’as toujours pas reçu ? Patiente encore un peu, l’e-mail peut prendre quelques minutes à arriver.",
      },
      error: {
        title: "Échec de l’envoi",
        body: "Réessaie plus tard.",
      },
    },
  },
  resetPasswordEmail: {
    success: {
      heading: "E-mail de réinitialisation envoyé.",
      body: "Nous t’avons envoyé un e-mail pour réinitialiser ton mot de passe. Consulte ta boîte de réception.",
      spamHint: "Tu n’as pas reçu d’e-mail ? Pense à vérifier dans tes spams.",
    },
    restart: {
      hint: "Si tu n’as toujours pas reçu l’e-mail, tu peux recommencer la procédure. Tu as peut-être utilisé une autre adresse e-mail.",
      button: "Réessayer",
    },
    error: {
      heading: "Échec de l’envoi",
      body: "Réessaie plus tard.",
      retryButton: "Réessayer",
      rateLimitBody:
        "Trop de tentatives. Attends quelques minutes avant de réessayer.",
    },
  },
  setPassword: {
    subtitle: "Tu peux maintenant définir ton mot de passe.",
    passwordLabel: "Mot de passe *",
    passwordRepeatLabel: "Confirmer le mot de passe *",
    submitButton: "Enregistrer le mot de passe",
    backToLogin: "Se connecter",
    successTitle: "Ton mot de passe a bien été enregistré.",
    successRedirectBody: "Tu vas être redirigé vers notre page d’accueil.",
    successRedirectLoginHint:
      "Tu pourras t’y connecter avec ton nouveau mot de passe.",
    successWaitHint: "Un instant…",
  },
  resetPasswordError: {
    expired: {
      heading: "Ce lien a expiré.",
      body: "Le lien pour réinitialiser ton mot de passe n’est plus valide. Retourne sur la page d’accueil et recommence la procédure en cliquant sur « Mot de passe oublié ? » dans l’espace de connexion.",
    },
    used: {
      heading: "Ce lien a déjà été utilisé.",
      body: "Ton mot de passe a déjà été réinitialisé avec ce lien. Si tu souhaites modifier ton mot de passe à nouveau, retourne sur la page d’accueil, puis clique sur « Mot de passe oublié ? » dans l’espace de connexion.",
    },
    generic: {
      heading: "Ce lien n’est pas valide.",
      body: "Ce lien ne peut pas être utilisé. Retourne sur la page d’accueil et recommence la procédure en cliquant sur « Mot de passe oublié ? » dans l’espace de connexion.",
    },
    backToHome: "Retour à la page d’accueil",
  },
  toasts: {
    missingRequiredFields:
      "Remplis tous les champs obligatoires pour continuer.",
  },
  validation: {
    passwordRequired: "Saisis un mot de passe.",
    passwordRepeatRequired: "Saisis à nouveau ton mot de passe.",
    firstNameTooLong: "Le prénom ne doit pas dépasser 30 caractères.",
    firstNameRequired: "Saisis ton prénom.",
    firstNameInvalidFormat: "Saisis un prénom valide.",
    lastNameTooLong: "Le nom ne doit pas dépasser 30 caractères.",
    lastNameRequired: "Saisis ton nom.",
    lastNameInvalidFormat: "Saisis un nom valide.",
    emailTooLong: "L’adresse e-mail est trop longue.",
    emailRequired: "Saisis une adresse e-mail.",
    emailInvalidFormat: "Saisis une adresse e-mail valide.",
    passwordMatchesEmail:
      "Le mot de passe ne doit pas être identique à l’adresse e-mail.",
    passwordMismatch: "Les mots de passe ne correspondent pas.",
  },
  passwordRequirements: {
    minimumLength: "Au moins 10 caractères",
    mixedCase: "Au moins une majuscule et une minuscule",
    hasNumber: "Au moins un chiffre",
    specialCharactersAllowed:
      "Les caractères spéciaux sont autorisés (p. ex. !@#$%^&*)",
  },
};
